/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

 document.addEventListener("DOMContentLoaded", function () {
    document.querySelectorAll(".btn_BIS").forEach(button => {
        button.addEventListener("click", function () {
            const bisTrigger = document.querySelector(".klaviyo-bis-trigger");
            if (bisTrigger) {
                bisTrigger.click();
            }
        });
    });
});

(function () {
  const buttonSelector = '.js-swym-wishlist-btn';
  let swat = null;
  let wishlistItems = [];
  let hasFetchedWishlist = false;
  let listenersBound = false;

  function normalizeUrl(url) {
    if (!url) return '';
    if (url.indexOf('//') === 0) return 'https:' + url;
    if (url.indexOf('http') === 0) return url;
    return window.location.origin + url;
  }

  function getActiveVariantId(button) {
    const form = button.closest('.js-product-form');
    const idInput = form ? form.querySelector('[name="id"]') : null;

    if (idInput && idInput.value) {
      return idInput.value;
    }

    return button.dataset.variantId || button.dataset.defaultVariantId || '';
  }

  function getButtonPayload(button) {
    const variantId = getActiveVariantId(button);
    if (!variantId) return null;

    const variantTitle = button.dataset.variantTitle || button.dataset.productTitle;
    const variantPrice = parseFloat(button.dataset.variantPrice || '0');
    const productId = parseInt(button.dataset.productId, 10);
    const variantNumericId = parseInt(variantId, 10);

    if (!productId || !variantNumericId) return null;

    return {
      epi: variantNumericId,
      empi: productId,
      du: normalizeUrl(button.dataset.productUrl),
      iu: normalizeUrl(button.dataset.productImage),
      pr: variantPrice,
      dt: button.dataset.productTitle,
      variants: [{ [variantTitle]: variantNumericId }]
    };
  }

  function setButtonState(button, state) {
    const labels = {
      default: button.dataset.labelDefault || 'Save for later',
      saved: button.dataset.labelSaved || 'Saved'
    };
    const labelText = state === 'saved' ? labels.saved : labels.default;
    const hiddenLabel = button.querySelector('.swym-custom-wishlist-btn__label');

    button.classList.toggle('is-active', state === 'saved');
    button.setAttribute('aria-pressed', state === 'saved' ? 'true' : 'false');
    button.setAttribute('aria-label', labelText);

    button.disabled = false;
    if (hiddenLabel) {
      hiddenLabel.textContent = labelText;
    }
  }

  function productIsWishlisted(button) {
    const payload = getButtonPayload(button);
    if (!payload) return false;

    return wishlistItems.some((item) => {
      return String(item.empi) === String(payload.empi) && String(item.epi) === String(payload.epi);
    });
  }

  function refreshButton(button) {
    if (!swat) {
      setButtonState(button, 'default');
      return;
    }

    if (!getButtonPayload(button)) {
      button.disabled = true;
      return;
    }

    if (!hasFetchedWishlist) {
      setButtonState(button, 'default');
      return;
    }

    setButtonState(button, productIsWishlisted(button) ? 'saved' : 'default');
  }

  function refreshButtons(root) {
    (root || document).querySelectorAll(buttonSelector).forEach((button) => refreshButton(button));
  }

  function fetchWishlist() {
    if (!swat || !swat.fetchWrtEventTypeET) return;

    swat.fetchWrtEventTypeET(function (products) {
      wishlistItems = Array.isArray(products) ? products : [];
      hasFetchedWishlist = true;
      refreshButtons(document);
    }, 4);
  }

  function rememberWishlistItem(payload) {
    const alreadySaved = wishlistItems.some((item) => {
      return String(item.empi) === String(payload.empi) && String(item.epi) === String(payload.epi);
    });

    if (!alreadySaved) {
      wishlistItems.push({
        empi: payload.empi,
        epi: payload.epi
      });
    }

    hasFetchedWishlist = true;
  }

  function forgetWishlistItem(payload) {
    wishlistItems = wishlistItems.filter((item) => {
      return !(String(item.empi) === String(payload.empi) && String(item.epi) === String(payload.epi));
    });

    hasFetchedWishlist = true;
  }

  function getSavedWishlistItem(payload) {
    return wishlistItems.find((item) => {
      return String(item.empi) === String(payload.empi) && String(item.epi) === String(payload.epi);
    }) || null;
  }

  function updateButtonsForVariantChange(event) {
    const productRoot = event.target.closest('.js-product');
    if (!productRoot) return;

    const variant = event.detail ? event.detail.variant : null;

    productRoot.querySelectorAll(buttonSelector).forEach((button) => {
      if (variant) {
        button.dataset.variantId = variant.id;
        button.dataset.variantTitle = variant.title || button.dataset.defaultVariantTitle || button.dataset.productTitle;
        button.dataset.variantPrice = typeof variant.price === 'number' ? (variant.price / 100) : (button.dataset.defaultVariantPrice || '');

        if (variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src) {
          button.dataset.productImage = variant.featured_media.preview_image.src;
        } else if (variant.featured_image && variant.featured_image.src) {
          button.dataset.productImage = variant.featured_image.src;
        } else {
          button.dataset.productImage = button.dataset.defaultProductImage || '';
        }
      } else {
        button.dataset.variantId = button.dataset.defaultVariantId || '';
        button.dataset.variantTitle = button.dataset.defaultVariantTitle || '';
        button.dataset.variantPrice = button.dataset.defaultVariantPrice || '';
        button.dataset.productImage = button.dataset.defaultProductImage || '';
      }

      refreshButton(button);
    });
  }

  function bindListeners() {
    if (listenersBound) return;
    listenersBound = true;

    document.addEventListener('click', function (event) {
      const button = event.target.closest(buttonSelector);
      if (!button) return;

      event.preventDefault();

      if (!swat) return;

      const payload = getButtonPayload(button);
      if (!payload) {
        button.disabled = true;
        return;
      }

      const savedItem = getSavedWishlistItem(payload);

      button.disabled = true;

      if (savedItem && typeof swat.removeFromWishList === 'function') {
        swat.removeFromWishList(savedItem, function () {
          forgetWishlistItem(payload);
          setButtonState(button, 'default');
        });

        return;
      }

      payload.et = 4;

      swat.addToWishList(payload, function () {
        rememberWishlistItem(payload);
        setButtonState(button, 'saved');
      });
    });

    document.addEventListener('on:quickbuy:after-open', function () {
      refreshButtons(document);
    });

    document.addEventListener('on:variant:change', updateButtonsForVariantChange);
  }

  function initializeSwymButtons(swatInstance) {
    swat = swatInstance || window._swat || null;
    bindListeners();
    refreshButtons(document);
    fetchWishlist();
  }

  bindListeners();
  document.addEventListener('DOMContentLoaded', function () {
    refreshButtons(document);
  });

  window.initializeSwymCustomWishlistButtons = initializeSwymButtons;

  if (!window.SwymCallbacks) {
    window.SwymCallbacks = [];
  }

  window.SwymCallbacks.push(initializeSwymButtons);
})();

(function () {
  const quickBuySelector = '.product-block__mobile-quickbuy';
  const openClass = 'is-open';
  const transitionDurationMs = 180;

  function clearCloseTimer(quickBuy) {
    if (!quickBuy || !quickBuy._quickBuyCloseTimer) return;
    window.clearTimeout(quickBuy._quickBuyCloseTimer);
    quickBuy._quickBuyCloseTimer = null;
  }

  function getSectionsToUpdate() {
    return ['page-header', 'cart-drawer']
      .map((selector) => document.querySelector(selector))
      .filter((element) => element && element.dataset.sectionId);
  }

  function closeQuickBuy(quickBuy) {
    if (!quickBuy) return;

    const toggle = quickBuy.querySelector('.js-mobile-quickbuy-toggle');
    const panel = quickBuy.querySelector('.product-block__mobile-quickbuy-panel');

    clearCloseTimer(quickBuy);
    quickBuy.classList.remove(openClass);
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
    }
    if (panel) {
      quickBuy._quickBuyCloseTimer = window.setTimeout(() => {
        if (!quickBuy.classList.contains(openClass)) {
          panel.hidden = true;
        }
        quickBuy._quickBuyCloseTimer = null;
      }, transitionDurationMs);
    }
  }

  function closeAllQuickBuys(except) {
    document.querySelectorAll(quickBuySelector).forEach((quickBuy) => {
      if (quickBuy !== except) {
        closeQuickBuy(quickBuy);
      }
    });
  }

  function openQuickBuy(quickBuy) {
    if (!quickBuy) return;

    const toggle = quickBuy.querySelector('.js-mobile-quickbuy-toggle');
    const panel = quickBuy.querySelector('.product-block__mobile-quickbuy-panel');

    closeAllQuickBuys(quickBuy);
    clearCloseTimer(quickBuy);
    quickBuy.classList.add(openClass);
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'true');
    }
    if (panel) {
      panel.hidden = false;
    }
  }

  async function addMobileQuickBuyVariant(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    const quickBuy = button.closest(quickBuySelector);
    const optionButtons = quickBuy ? quickBuy.querySelectorAll('.js-mobile-quickbuy-variant') : [];
    const sectionsToUpdate = getSectionsToUpdate();
    const sectionIds = sectionsToUpdate.map((element) => element.dataset.sectionId);
    const formData = new FormData();

    if (quickBuy) {
      quickBuy.classList.add('is-submitting');
    }
    button.classList.add('is-loading');

    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('sections_url', window.location.pathname);
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }

    try {
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || response.status);
      }

      if (data.sections && sectionsToUpdate.length === Object.keys(data.sections).length) {
        sectionsToUpdate.forEach((element) => {
          element.updateFromCartChange(data.sections[element.dataset.sectionId]);
        });
      } else {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
          bubbles: true
        }));
      }

      closeQuickBuy(quickBuy);

      if (theme.settings.afterAddToCart === 'page') {
        window.location.href = theme.routes.cart;
        return;
      }

      const cartDrawer = document.querySelector('.js-cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open(button);
      } else {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
          detail: {
            opener: button
          }
        }));
      }
    } catch (error) {
      console.warn(error);
    } finally {
      quickBuy?.classList.remove('is-submitting');
      button.classList.remove('is-loading');
    }
  }

  function rememberInitialDisabledState() {
    document.querySelectorAll('.js-mobile-quickbuy-variant').forEach((button) => {
      if (!button.dataset.initialized) {
        button.dataset.initialized = 'true';
        button.dataset.initiallyDisabled = button.disabled ? 'true' : 'false';
      }
    });
  }

  rememberInitialDisabledState();
  document.addEventListener('DOMContentLoaded', rememberInitialDisabledState);
  document.addEventListener('shopify:section:load', rememberInitialDisabledState);

  document.addEventListener('click', function (event) {
    const toggle = event.target.closest('.js-mobile-quickbuy-toggle');
    if (toggle) {
      event.preventDefault();
      const quickBuy = toggle.closest(quickBuySelector);
      if (quickBuy && quickBuy.classList.contains(openClass)) {
        closeQuickBuy(quickBuy);
      } else {
        openQuickBuy(quickBuy);
      }
      return;
    }

    const closeButton = event.target.closest('.js-mobile-quickbuy-close');
    if (closeButton) {
      event.preventDefault();
      closeQuickBuy(closeButton.closest(quickBuySelector));
      return;
    }

    const variantButton = event.target.closest('.js-mobile-quickbuy-variant');
    if (variantButton) {
      event.preventDefault();
      addMobileQuickBuyVariant(variantButton);
      return;
    }

    if (!event.target.closest(quickBuySelector)) {
      closeAllQuickBuys();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      closeAllQuickBuys();
    }
  });

  window.addEventListener('on:debounced-resize', function () {
    if (window.innerWidth >= 768) {
      closeAllQuickBuys();
    }
  });
})();

(function () {
  var LL_API = 'https://api.loyaltylion.com/headless/2025-06';
  var banner = null;
  var mergeTimer = null;
  var cachedVoucher = undefined; // undefined = not yet fetched; null = fetched, no voucher

  function getBanner() {
    if (!banner) banner = document.getElementById('ll-rewards-banner');
    return banner;
  }

  function getCustomerId() {
    var ll = window.loyaltylion;
    if (ll) {
      var opts = ll._options || ll.options || {};
      if (opts.customer && opts.customer.id) return String(opts.customer.id);
    }
    return window.llCustomer ? String(window.llCustomer.id) : null;
  }

  function showBanner(redeemable) {
    var el = getBanner();
    if (!el) return;
    el.querySelector('.ll-rewards-banner__code').textContent = redeemable.code || '';
    el.querySelector('.ll-rewards-banner__amount').textContent =
      (redeemable.discount && redeemable.discount.formatted) || '';
    el.hidden = false;
  }

  function hideBanner() {
    var el = getBanner();
    if (el) el.hidden = true;
  }

  function fetchAndDisplay() {
    // Serve from cache instantly on repeat opens
    if (cachedVoucher !== undefined) {
      if (cachedVoucher) { showBanner(cachedVoucher); } else { hideBanner(); }
      return;
    }

    var cfg = window.__rc;
    var customerId = getCustomerId();

    if (!cfg || !cfg.t || !cfg.n) { hideBanner(); return; }
    if (!customerId) { hideBanner(); return; }

    fetch(LL_API + '/' + cfg.n + '/customers/' + customerId + '?channel=web', {
      headers: { 'Authorization': 'Bearer ' + cfg.t }
    })
      .then(function (res) {
        // The session token is minted at page render. Shopify serves cached pages,
        // so a long-lived tab can outlive its token; a reload mints a fresh one.
        if (res.status === 401) return Promise.reject('session token rejected (expired? reload)');
        if (!res.ok) return Promise.reject('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var claimed = (data && data.customer && data.customer.claimed_rewards) || [];
        var voucher = claimed.find(function (r) {
          return r.state === 'approved' &&
            r.redeemable &&
            r.redeemable.kind === 'cart_discount_voucher' &&
            r.redeemable.usage_status === 'not_used';
        });
        cachedVoucher = voucher ? voucher.redeemable : null;
        if (cachedVoucher) { showBanner(cachedVoucher); } else { hideBanner(); }
      })
      .catch(function (err) {
        console.error('[LL Rewards] Fetch failed:', err);
        hideBanner();
      });
  }

  function debouncedFetch() {
    banner = null;        // DOM replaced by AJAX merge — drop cached reference
    cachedVoucher = undefined; // cart changed — re-fetch in case reward was used
    clearTimeout(mergeTimer);
    mergeTimer = setTimeout(fetchAndDisplay, 400);
  }

  document.addEventListener('on:cart-drawer:after-open', fetchAndDisplay);
  document.addEventListener('on:cart:after-merge', debouncedFetch);

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('.ll-rewards-banner__copy');
    if (!btn) return;
    var el = getBanner();
    var code = el && el.querySelector('.ll-rewards-banner__code').textContent;
    if (!code) return;
    navigator.clipboard.writeText(code).then(function () {
      btn.textContent = 'Copied!';
      setTimeout(function () { btn.textContent = 'Copy'; }, 2000);
    });
  });
})();
